import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { ComponentService } from '../../../services/component/component.service';
import { ComponentInputBase } from '../../../models/ComponentInputBase.model';
import { ComponentDataSourceService } from '../../../services/component-data-source/component-data-source.service';
import { ComponentDataDestService } from '../../../services/component-data-dest/component-data-dest.service';
import { createGroupCalendarEvent } from '../../../services/component-data-dest/data/add-event';
import { environment } from '../../../environment/environment';
import {
  GroupCalendarDayRow,
  GroupCalendarEventCell,
  GroupCalendarDragEventData,
  GroupCalendarDropCellData,
  GroupCalendarLocationOption,
  GroupCalendarMonthData,
  GroupCalendarSettings,
  GroupCalendarStatsLocationColumn,
  GroupCalendarTemplateOption,
  GroupCalendarTemplateStatRow,
} from './group-calendar-settings.interface';
import {
  DayCellActionDialogComponent,
  DAY_CELL_ACTION_DIALOG_CONFIG,
} from './day-cell-action-dialog.component';
import { GroupCalendarSettingsDialogComponent } from './group-calendar-settings-dialog.component';
import { GroupPickDialogComponent } from './group-pick-dialog.component';
import { ConfirmResetDialogComponent } from '../duna-dashboard/confirm-reset-dialog.component';
import {
  extractLocationOptions,
  fetchEventTemplates,
  loadGroupCalendarSettingsFromOrg,
  LOCATIONS_PAGE_SIZE,
  buildEventLocationPatch,
  buildOrderedSelectedLocations,
  buildStatsLocationColumns,
  buildTemplateLocationStatsFromApi,
  readGroupCalendarSettings,
  resolveGroupIdForDate,
  resolveGroupIdsForDate,
  setRowUserGroupIds,
  saveGroupCalendarSettingsToOrg,
  shiftEventDatesToDay,
  getWeeklyPlanTemplateId,
} from './group-calendar-settings.utils';
import {
  GroupCalendarGridComponent,
  GroupCalendarDropEvent,
} from './group-calendar-grid.component';

const MONTH_NAMES = [
  'Januar',
  'Februar',
  'Marts',
  'April',
  'Maj',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'December',
];

@Component({
  selector: 'app-duna-events-group-calendar',
  standalone: true,
  imports: [MatSnackBarModule, GroupCalendarGridComponent],
  templateUrl: './duna-events-group-calendar.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './duna-events-group-calendar.component.scss',
})
export class DunaEventsGroupCalendarComponent
  extends ComponentInputBase
  implements OnInit, OnDestroy
{
  viewYear = new Date().getFullYear();
  viewMonth = new Date().getMonth();
  monthData: GroupCalendarMonthData | null = null;
  templateStats: GroupCalendarTemplateStatRow[] = [];
  locationOptions: GroupCalendarLocationOption[] = [];
  statsLocationColumns: GroupCalendarStatsLocationColumn[] = buildStatsLocationColumns(
    []
  );
  templates: GroupCalendarTemplateOption[] = [];
  groups: { group_id: number; name: string }[] = [];
  groupsEnabled = false;
  isLoading = false;
  /** Per-day group defaults before events exist */
  dayGroupDefaults: Record<string, number[]> = {};
  private readonly destroy$ = new Subject<void>();
  private initialized = false;

  constructor(
    protected override componentService: ComponentService,
    private dataSourceService: ComponentDataSourceService,
    private dataDestService: ComponentDataDestService,
    private http: HttpClient,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private router: Router
  ) {
    super(componentService);
    this.inputs = {
      ...this.inputs,
      calendarLocationGuids: { type: 'json', value: [] as string[] },
      showGroupColumn: { type: 'boolean', value: true },
      templateColors: { type: 'json', value: {} as Record<string, string> },
      weeklyPlanByYear: {
        type: 'json',
        value: {} as Record<
          string,
          Record<string, Record<string, number | null>>
        >,
      },
      weeklyPlanYear: { type: 'number', value: new Date().getFullYear() },
      groupRotationByYear: {
        type: 'json',
        value: {} as Record<string, number[]>,
      },
      groupRotationYear: { type: 'number', value: new Date().getFullYear() },
      weekRotationByYear: {
        type: 'json',
        value: {} as GroupCalendarSettings['weekRotationByYear'],
      },
      weekRotationYear: { type: 'number', value: new Date().getFullYear() },
      activeRotationModeByYear: {
        type: 'json',
        value: {} as GroupCalendarSettings['activeRotationModeByYear'],
      },
      settingsRotationTab: { type: 'text', value: 'group' as const },
      weekRotationWeeklyPlanByYear: {
        type: 'json',
        value: {} as GroupCalendarSettings['weekRotationWeeklyPlanByYear'],
      },
    };
  }

  ngOnInit(): void {
    void this.init();
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        if (this.initialized) {
          void this.refreshCalendar();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async waitForAuthReady(maxMs = 5000): Promise<void> {
    const started = Date.now();
    while (Date.now() - started < maxMs) {
      if (this.dataSourceService.authService.getCurrentUser()) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
  }

  private async init(): Promise<void> {
    await this.waitForAuthReady();
    await this.loadGroupsEnabled();
    await this.loadPersistedSettings();
    await Promise.all([
      this.loadLocationNames(),
      this.loadTemplates(),
      this.loadGroups(),
    ]);
    await this.loadMonth();
    this.initialized = true;
  }

  private async refreshCalendar(): Promise<void> {
    await this.waitForAuthReady();
    await this.loadPersistedSettings();
    await this.loadLocationNames();
    await this.loadMonth();
  }

  private async loadPersistedSettings(): Promise<void> {
    const orgSettings = await loadGroupCalendarSettingsFromOrg(
      this.dataSourceService,
      this.viewYear
    );
    if (orgSettings) {
      this.applySettings(orgSettings);
    }
  }

  private applySettings(settings: GroupCalendarSettings): void {
    this.setInput('calendarLocationGuids', settings.calendarLocationGuids);
    this.setInput('showGroupColumn', settings.showGroupColumn);
    this.setInput('templateColors', settings.templateColors);
    this.setInput('weeklyPlanByYear', settings.weeklyPlanByYear);
    this.setInput('weeklyPlanYear', settings.weeklyPlanYear);
    this.setInput('groupRotationByYear', settings.groupRotationByYear);
    this.setInput('groupRotationYear', settings.groupRotationYear);
    this.setInput('weekRotationByYear', settings.weekRotationByYear);
    this.setInput('weekRotationYear', settings.weekRotationYear);
    this.setInput('activeRotationModeByYear', settings.activeRotationModeByYear);
    this.setInput('settingsRotationTab', settings.settingsRotationTab);
    this.setInput(
      'weekRotationWeeklyPlanByYear',
      settings.weekRotationWeeklyPlanByYear
    );
    this.dayGroupDefaults = Object.fromEntries(
      Object.entries(settings.dayGroupByDate).map(([key, ids]) => [key, [...ids]])
    );
  }

  private buildSettingsSnapshot(): GroupCalendarSettings {
    return {
      ...this.settings,
      dayGroupByDate: Object.fromEntries(
        Object.entries(this.dayGroupDefaults).map(([key, ids]) => [key, [...ids]])
      ),
    };
  }

  private async persistGroupCalendarSettings(
    settings: GroupCalendarSettings
  ): Promise<void> {
    await saveGroupCalendarSettingsToOrg(
      this.dataDestService,
      this.dataSourceService,
      settings
    );
  }

  get settings(): GroupCalendarSettings {
    return {
      ...readGroupCalendarSettings((key) => this.getInput(key), this.viewYear),
      dayGroupByDate: Object.fromEntries(
        Object.entries(this.dayGroupDefaults).map(([key, ids]) => [key, [...ids]])
      ),
    };
  }

  get showGroupColumn(): boolean {
    return this.groupsEnabled && this.settings.showGroupColumn;
  }

  get monthLabel(): string {
    return `${MONTH_NAMES[this.viewMonth]} ${this.viewYear}`;
  }


  // --- Grid output event handlers ---

  onCellClickFromGrid(event: {
    row: GroupCalendarDayRow;
    locationKey: string;
    locationLabel: string;
  }): void {
    void this.onCellClick(event.row, event.locationKey, event.locationLabel);
  }

  async onEventDroppedFromGrid(drop: GroupCalendarDropEvent): Promise<void> {
    const { source, target } = drop;
    const targetGroupId = await this.resolveTargetGroupIdForMove(
      source,
      target.row
    );
    if (targetGroupId == null) return;
    const sourceDateLabel = `${source.row.dayOfMonth}. ${
      MONTH_NAMES[this.viewMonth]
    }`;
    const targetDateLabel = `${target.row.dayOfMonth}. ${
      MONTH_NAMES[this.viewMonth]
    }`;
    const sourceLocationLabel =
      source.locationKey === 'other'
        ? 'Andet'
        : this.getLocationName(source.locationKey);
    const targetLocationLabel =
      target.locationKey === 'other'
        ? 'Andet'
        : this.getLocationName(target.locationKey);
    const details = [
      `Event: ${source.event.name}`,
      `Fra: ${sourceDateLabel} · ${sourceLocationLabel}`,
      `Til: ${targetDateLabel} · ${targetLocationLabel}`,
    ];
    if (targetGroupId != null && targetGroupId !== source.event.user_group_id) {
      const groupName =
        this.groups.find((g) => g.group_id === targetGroupId)?.name ??
        `Gruppe ${targetGroupId}`;
      details.push(`Ny gruppe: ${groupName}`);
    }
    const confirmed = await firstValueFrom(
      this.dialog
        .open(ConfirmResetDialogComponent, {
          width: '520px',
          data: {
            title: 'Flyt event?',
            message:
              'Er du sikker på, at du vil flytte dette event til den nye dag?',
            details,
            confirmText: 'Ja, flyt event',
            cancelText: 'Annuller',
            confirmColor: 'primary',
          },
        })
        .afterClosed()
    );
    if (!confirmed) return;
    try {
      await this.moveEventToCell(source, target, targetGroupId);
      this.snackBar.open('Event flyttet', 'Luk', { duration: 3000 });
      await this.loadMonth();
    } catch {
      this.snackBar.open('Kunne ikke flytte event', 'Luk', { duration: 4000 });
    }
  }

  onChipClickFromGrid(event: {
    mouseEvent: MouseEvent;
    row: GroupCalendarDayRow;
    locationKey: string;
    locationLabel: string;
  }): void {
    const { mouseEvent, row, locationKey, locationLabel } = event;
    const cell = row.cells[locationKey];
    if (cell && (mouseEvent.ctrlKey || mouseEvent.metaKey)) {
      window.open(
        `${window.location.origin}/edit-event/${cell.guid}`,
        '_blank',
        'noopener,noreferrer'
      );
      return;
    }
    void this.onCellClick(row, locationKey, locationLabel);
  }

  private async loadGroupsEnabled(): Promise<void> {
    try {
      const result = await firstValueFrom(
        this.dataSourceService.executeDataSource('duna_groups_enabled', {})!
      );
      this.groupsEnabled = (result as { visible?: boolean })?.visible === true;
    } catch {
      this.groupsEnabled = false;
    }
  }

  private async loadLocationNames(): Promise<void> {
    try {
      const result = await firstValueFrom(
        this.dataSourceService.executeDataSource('locations', {
          filters: [
            { column: 'pageSize', value: LOCATIONS_PAGE_SIZE, operator: 'eq' },
            { column: 'pageIndex', value: 0, operator: 'eq' },
            // Only guid + name are used: skip per-user progress/validation on the server.
            { column: 'lightweight', value: 1, operator: 'eq' },
          ],
        })!
      );
      const items = result?.items ?? [];
      const all = extractLocationOptions(Array.isArray(items) ? items : []);
      this.locationOptions = buildOrderedSelectedLocations(
        this.settings.calendarLocationGuids,
        all
      );
      this.statsLocationColumns = buildStatsLocationColumns(
        this.locationOptions
      );
    } catch {
      this.locationOptions = [];
      this.statsLocationColumns = buildStatsLocationColumns([]);
    }
  }

  private async loadTemplates(): Promise<void> {
    try {
      this.templates = await fetchEventTemplates(this.dataSourceService);
    } catch {
      this.templates = [];
    }
  }

  private async loadGroups(): Promise<void> {
    try {
      const result = await firstValueFrom(
        this.dataSourceService.executeDataSource('duna_user_groups', {})!
      );
      const items = result?.items ?? [];
      this.groups = (Array.isArray(items) ? items : []).map(
        (g: { group_id: number; name: string }) => ({
          group_id: g.group_id,
          name: g.name,
        })
      );
    } catch {
      this.groups = [];
    }
  }

  async loadMonth(): Promise<void> {
    this.isLoading = true;
    this.cdr.markForCheck();
    try {
      const baseQueryParams = {
        year: this.viewYear,
        month: this.viewMonth,
        calendarLocationGuids: this.settings.calendarLocationGuids,
      };
      const [monthResult, statsResult] = await Promise.all([
        firstValueFrom(
          this.dataSourceService.executeDataSource('group_calendar_events', {
            queryParams: baseQueryParams,
          })!
        ),
        firstValueFrom(
          this.dataSourceService.executeDataSource('group_calendar_stats', {
            queryParams: baseQueryParams,
          })!
        ),
      ]);
      this.monthData = monthResult as GroupCalendarMonthData;
      this.templateStats = buildTemplateLocationStatsFromApi(
        statsResult.items ?? [],
        this.statsLocationColumns,
        this.templates,
        this.settings.templateColors
      );
      this.applyDayGroupDefaults();
    } catch {
      this.monthData = null;
      this.templateStats = [];
    } finally {
      this.isLoading = false;
      this.cdr.markForCheck();
    }
  }

  private applyDayGroupDefaults(): void {
    if (!this.monthData) return;
    for (const row of this.monthData.days) {
      const resolved = resolveGroupIdsForDate(this.settings, row.dateKey);
      if (resolved.length > 0) {
        setRowUserGroupIds(row, resolved);
        row.user_group_name =
          this.groups.find((gr) => gr.group_id === resolved[0])?.name ?? null;
      } else if (!row.user_group_ids) {
        row.user_group_ids = [];
      }
    }
  }

  private setRowGroupsFromIds(
    row: GroupCalendarDayRow,
    ids: number[]
  ): void {
    setRowUserGroupIds(row, ids);
    row.user_group_name =
      ids[0] != null
        ? this.groups.find((gr) => gr.group_id === ids[0])?.name ?? null
        : null;
  }

  prevMonth(): void {
    if (this.viewMonth === 0) {
      this.viewMonth = 11;
      this.viewYear--;
    } else {
      this.viewMonth--;
    }
    void this.loadMonth();
  }

  nextMonth(): void {
    if (this.viewMonth === 11) {
      this.viewMonth = 0;
      this.viewYear++;
    } else {
      this.viewMonth++;
    }
    void this.loadMonth();
  }

  getLocationName(guid: string): string {
    return (
      this.locationOptions.find((l) => l.guid === guid)?.name ??
      guid.slice(0, 6)
    );
  }

  getDefaultTemplateIdForLocation(
    row: GroupCalendarDayRow,
    locationKey: string
  ): number | null {
    return getWeeklyPlanTemplateId(this.settings, locationKey, row.dateKey);
  }

  resolveGroupIds(row: GroupCalendarDayRow): number[] {
    const fromSettings = resolveGroupIdsForDate(this.settings, row.dateKey);
    if (fromSettings.length > 0) return fromSettings;
    if (row.user_group_ids?.length) return [...row.user_group_ids];
    if (row.user_group_id != null) return [row.user_group_id];
    return [];
  }

  resolveGroupId(row: GroupCalendarDayRow): number | null {
    const ids = this.resolveGroupIds(row);
    return ids.length > 0 ? ids[0] : null;
  }

  private async pickGroupFromList(
    title: string,
    message: string,
    groupIds: number[]
  ): Promise<number | null> {
    const options = groupIds
      .map((id) => this.groups.find((g) => g.group_id === id))
      .filter((g): g is { group_id: number; name: string } => g != null);
    if (options.length === 0) return null;
    if (options.length === 1) return options[0].group_id;
    return firstValueFrom(
      this.dialog
        .open(GroupPickDialogComponent, {
          width: '420px',
          data: { title, message, groups: options },
        })
        .afterClosed()
    );
  }

  private async resolveTargetGroupIdForMove(
    source: GroupCalendarDragEventData,
    targetRow: GroupCalendarDayRow
  ): Promise<number | null> {
    const targetGroups = this.resolveGroupIds(targetRow);
    const sourceGroupId = source.event.user_group_id ?? null;
    if (sourceGroupId != null && targetGroups.includes(sourceGroupId)) {
      return sourceGroupId;
    }
    if (targetGroups.length === 1) return targetGroups[0];
    if (targetGroups.length === 0) return null;
    return this.pickGroupFromList(
      'Vælg gruppe',
      'Dagen har flere grupper. Hvilken gruppe skal eventet tilknyttes?',
      targetGroups
    );
  }

  private rowHasEvents(row: GroupCalendarDayRow): boolean {
    return Object.values(row.cells).some((cell) => cell != null);
  }

  async openSettings(): Promise<void> {
    const ref = this.dialog.open(GroupCalendarSettingsDialogComponent, {
      width: '96vw',
      height: '92vh',
      maxWidth: '96vw',
      maxHeight: '92vh',
      panelClass: 'group-calendar-settings-panel',
      data: {
        settings: this.settings,
        groupsEnabled: this.groupsEnabled,
        groups: this.groups,
      },
    });

    const newSettings = await firstValueFrom(ref.afterClosed());
    if (!newSettings) return;

    this.applySettings(newSettings);
    try {
      await this.persistGroupCalendarSettings(newSettings);
      this.snackBar.open('Indstillinger gemt', 'Luk', { duration: 3000 });
    } catch {
      this.snackBar.open('Indstillinger kunne ikke gemmes — prøv igen', 'Luk', {
        duration: 5000,
      });
    }
    await this.loadLocationNames();
    await this.loadMonth();
    this.cdr.markForCheck();
  }

  async onCellClick(
    row: GroupCalendarDayRow,
    locationKey: string,
    locationLabel: string
  ): Promise<void> {
    const dayGroupIds = this.resolveGroupIds(row);
    if (this.showGroupColumn && dayGroupIds.length === 0) {
      this.snackBar.open(
        'Vælg mindst én gruppe for dagen før du opretter events',
        'Luk',
        { duration: 4000 }
      );
      return;
    }

    const existing = row.cells[locationKey];
    const dateLabel = `${row.dayOfMonth}. ${MONTH_NAMES[this.viewMonth]} ${
      this.viewYear
    }`;

    const ref = this.dialog.open(DayCellActionDialogComponent, {
      ...DAY_CELL_ACTION_DIALOG_CONFIG,
      data: {
        dateLabel,
        locationLabel,
        dateKey: row.dateKey,
        locationKey,
        existingEvent: existing,
        templates: this.templates,
        templateColors: this.settings.templateColors,
        defaultTemplateId: this.getDefaultTemplateIdForLocation(
          row,
          locationKey
        ),
        dayGroupOptions: dayGroupIds
          .map((id) => this.groups.find((g) => g.group_id === id))
          .filter((g): g is { group_id: number; name: string } => g != null),
      },
    });

    const result = await firstValueFrom(ref.afterClosed());
    if (!result) return;

    if (result.action === 'remove' && existing) {
      await this.deleteEvent(existing.guid);
      this.snackBar.open('Event slettet', 'Luk', { duration: 3000 });
      await this.loadMonth();
      return;
    }

    if (result.action === 'create' && result.template) {
      if (existing) {
        await this.deleteEvent(existing.guid);
      }
      const date = new Date(row.dateKey);
      const groupId =
        result.groupId ??
        existing?.user_group_id ??
        (dayGroupIds.length === 1 ? dayGroupIds[0] : null);
      if (groupId == null) {
        this.snackBar.open('Vælg en gruppe for eventet', 'Luk', {
          duration: 4000,
        });
        return;
      }
      try {
        await createGroupCalendarEvent(
          this.dataDestService,
          this.dataSourceService,
          result.template.template_data,
          date,
          locationKey,
          groupId,
          result.template.template_id,
          result.formValues
        );
        this.snackBar.open('Event oprettet', 'Luk', { duration: 3000 });
        await this.loadMonth();
      } catch {
        this.snackBar.open('Kunne ikke oprette event', 'Luk', {
          duration: 4000,
        });
      }
    }
  }

  private async deleteEvent(guid: string): Promise<void> {
    await firstValueFrom(
      this.http.patch(`${environment.apiUrl}/events/${guid}`, {
        event: { deleted: true, is_active: false },
      })
    );
  }

  private async patchEvent(
    guid: string,
    eventPatch: Record<string, unknown>
  ): Promise<void> {
    await firstValueFrom(
      this.http.patch(`${environment.apiUrl}/events/${guid}`, {
        event: eventPatch,
      })
    );
  }

  private async moveEventToCell(
    source: GroupCalendarDragEventData,
    target: GroupCalendarDropCellData,
    targetGroupId: number | null
  ): Promise<void> {
    const dates = shiftEventDatesToDay(
      source.event.start_date,
      source.event.end_date,
      target.row.dateKey
    );
    const locationChanged = source.locationKey !== target.locationKey;
    const patch: Record<string, unknown> = {
      ...dates,
      user_group_id: targetGroupId,
    };
    if (locationChanged) {
      Object.assign(patch, buildEventLocationPatch(target.locationKey));
    }
    await this.patchEvent(source.event.guid, patch);
  }

  private dayEventsForRow(row: GroupCalendarDayRow): GroupCalendarEventCell[] {
    return (
      this.monthData?.events.filter((e) =>
        e.start_date.startsWith(row.dateKey)
      ) ?? []
    );
  }

  private groupIdsUsedByDayEvents(row: GroupCalendarDayRow): Set<number> {
    const used = new Set<number>();
    for (const ev of this.dayEventsForRow(row)) {
      if (ev.user_group_id != null && ev.user_group_id > 0) {
        used.add(ev.user_group_id);
      }
    }
    return used;
  }

  async onGroupChange(row: GroupCalendarDayRow, value: number[]): Promise<void> {
    const previousGroupIds = [...(row.user_group_ids ?? [])];
    const groupIds = (Array.isArray(value) ? value : [])
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id) && id > 0);
    const nextGroupIds = [...new Set(groupIds)];

    const usedByEvents = this.groupIdsUsedByDayEvents(row);
    const removedInUse = previousGroupIds.filter(
      (id) => !nextGroupIds.includes(id) && usedByEvents.has(id)
    );
    if (removedInUse.length > 0) {
      this.setRowGroupsFromIds(row, previousGroupIds);
      this.snackBar.open(
        'Kan ikke fjerne en gruppe der har events på dagen',
        'Luk',
        { duration: 4000 }
      );
      this.cdr.markForCheck();
      return;
    }

    if (nextGroupIds.length === 0 && this.rowHasEvents(row)) {
      this.setRowGroupsFromIds(row, previousGroupIds);
      this.snackBar.open(
        'Kan ikke fjerne alle grupper fra dage med events',
        'Luk',
        { duration: 4000 }
      );
      this.cdr.markForCheck();
      return;
    }

    if (nextGroupIds.length === 0) {
      delete this.dayGroupDefaults[row.dateKey];
    } else {
      this.dayGroupDefaults[row.dateKey] = nextGroupIds;
    }

    this.setRowGroupsFromIds(row, nextGroupIds);

    try {
      await this.persistGroupCalendarSettings(this.buildSettingsSnapshot());
    } catch {
      this.setRowGroupsFromIds(row, previousGroupIds);
      this.snackBar.open('Gruppevalg kunne ikke gemmes', 'Luk', {
        duration: 4000,
      });
      this.cdr.markForCheck();
      return;
    }

    this.cdr.markForCheck();
  }
}
